import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useDocument } from '../../Pages/SpreadsheetContainer/documentInfoContex';
import './styles.css';
import { workwiseServices } from '../../Workw-Api/api';
import { templateService } from '../../utils/api';

const RenameModal = ({ visible, onClose }) => {

    const { id } = useParams();
    const { isTemplateDocument, templateData, setTemplateData, documentInfo, setDocumentInfo, savingData, setSavingData } = useDocument();
    const [newName, setNewName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        // prefill and reset states on open
        if (visible) {
            const currentName = isTemplateDocument ? templateData?.name : documentInfo?.name;
            setNewName(currentName || '');
            setError('');
            // focus and select the input for quick rename
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 0);
        }
    }, [visible, documentInfo, templateData, isTemplateDocument]);

    const handleRename = async () => {
        const trimmed = newName.trim();
        if (!trimmed) {
            setError('Name cannot be empty');
            message.error('Name cannot be empty');
            return;
        }

        // no length limit validation - any non-empty value is accepted
        try {
            setLoading(true);
            if (isTemplateDocument) {
                await templateService.updateTemplate({ templateId: id, data: { name: trimmed, docType: 'mileboard' }, dispatch: setSavingData });
                message.success('Template renamed successfully');
            } else {
                const payload = {
                    attachmentId: id,
                    name: trimmed,
                    privacyId: documentInfo?.privacyId || 1,
                    members: documentInfo?.members || []
                };
                await workwiseServices.updateDocumentName(payload);
                message.success('Document renamed successfully');
            }
            if (isTemplateDocument) {
                setTemplateData({ ...templateData, name: trimmed });
            } else {
                setDocumentInfo({ ...documentInfo, name: trimmed });
            }
            onClose();
        } catch (e) {
            message.error('Failed to rename document. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };

    const isEmpty = newName.trim() === '';
    const canRename = !isEmpty && !loading;

    return (
        <Modal
            title={<span className="modal-title"><EditOutlined /> {isTemplateDocument ? 'Rename Template' : 'Rename Document'}</span>}
            visible={visible}
            onOk={handleRename}
            onCancel={onClose}
            okText="Rename"
            cancelText="Cancel"
            className="rename-modal"
            centered
            width={420}
            okButtonProps={{ disabled: !canRename, loading }}
        >
            <div className="modal-body">
                <label className="input-label">New {isTemplateDocument ? 'Template' : 'Document'} Name <span className="required">*</span></label>
                <Input
                    ref={inputRef}
                    value={newName}
                    onChange={(e) => {
                        const value = e.target.value;
                        setError('');
                        setNewName(value);
                    }}
                    placeholder={`Enter new ${isTemplateDocument ? 'template' : 'document'} name`}
                    onPressEnter={handleRename}
                    className="rename-input"
                    aria-label={`New ${isTemplateDocument ? 'template' : 'document'} name`}
                    aria-describedby={error ? 'rename-helper-text' : undefined}
                    aria-invalid={!!error}
                    allowClear
                />
                {error && (
                    <div className="rename-meta">
                        <span className="error-text" id="rename-helper-text">{error}</span>
                    </div>
                )}
                <p className="helper-text">
                    This will update the {isTemplateDocument ? 'template' : 'document'} name for all collaborators.
                </p>
            </div>
        </Modal>
    );
};

export default RenameModal;